/**
 * <instagram-gallery>
 *  - Progressive reveal: only the first batch of thumbnails is requested;
 *    hidden ones keep data-src/data-srcset until revealed.
 *  - Lightbox (native <dialog>): prev/next buttons, ArrowLeft/ArrowRight,
 *    swipe on touch, Esc / click-outside / close button to dismiss.
 *    Navigation spans ALL photos, including ones not yet revealed in the grid.
 */
if (!customElements.get('instagram-gallery')) {
  class InstagramGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.cells = Array.from(this.querySelectorAll('.instagram-gallery__cell'));
      this.items = this.cells.map((cell) => cell.querySelector('.instagram-gallery__item'));
      this.batch = parseInt(this.dataset.batch, 10) || 12;
      this.moreButton = this.querySelector('[data-more]');
      this.status = this.querySelector('[data-status]');
      this.dialog = this.querySelector('dialog');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.counter = this.querySelector('[data-counter]');
      this.index = 0;

      this.querySelector('.instagram-gallery__grid').addEventListener('click', (event) => {
        const item = event.target.closest('.instagram-gallery__item');
        if (item) this.open(this.items.indexOf(item));
      });

      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => this.showMore(true));
      }

      if (!this.dialog) return;

      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());

      // Click on the empty area around the photo closes the viewer.
      this.querySelector('[data-stage]').addEventListener('click', (event) => {
        if (!event.target.closest('img')) this.dialog.close();
      });

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(1); }
      });

      let startX = 0;
      let startY = 0;
      this.dialog.addEventListener('touchstart', (event) => {
        startX = event.changedTouches[0].clientX;
        startY = event.changedTouches[0].clientY;
      }, { passive: true });
      this.dialog.addEventListener('touchend', (event) => {
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(dx < 0 ? 1 : -1);
      }, { passive: true });

      const done = () => this.lightboxImage.classList.remove('is-loading');
      this.lightboxImage.addEventListener('load', done);
      this.lightboxImage.addEventListener('error', done);

      this.dialog.addEventListener('close', () => this.onClose());
    }

    /* ---------- progressive reveal ---------- */

    hydrate(cell) {
      const img = cell.querySelector('img[data-src]');
      if (!img) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }

    showMore(moveFocus) {
      const next = this.cells.filter((cell) => cell.hidden).slice(0, this.batch);
      next.forEach((cell) => {
        cell.hidden = false;
        this.hydrate(cell);
      });
      this.updateStatus();
      if (moveFocus && next[0]) next[0].querySelector('button').focus();
    }

    updateStatus() {
      const visible = this.cells.filter((cell) => !cell.hidden).length;
      if (this.status) this.status.textContent = `Showing ${visible} of ${this.cells.length} photos`;
      if (this.moreButton && visible >= this.cells.length) this.moreButton.hidden = true;
    }

    /* ---------- lightbox ---------- */

    srcFor(item) {
      const wide = window.innerWidth * (window.devicePixelRatio || 1) > 1100;
      return wide ? item.dataset.srcLarge : item.dataset.srcSmall;
    }

    open(index) {
      if (index < 0) return;
      this.index = index;
      this.render();
      this.dialog.showModal();
      document.documentElement.classList.add('instagram-lightbox-open');
    }

    go(delta) {
      const n = this.items.length;
      this.index = (this.index + delta + n) % n;
      this.render();
    }

    render() {
      const item = this.items[this.index];
      const img = this.lightboxImage;
      img.classList.add('is-loading');
      img.alt = item.dataset.alt;
      img.src = this.srcFor(item);
      this.counter.textContent = `${this.index + 1} / ${this.items.length}`;

      // Warm the cache for the neighbours so arrow presses feel instant.
      [1, -1].forEach((step) => {
        const n = this.items.length;
        const neighbour = this.items[(this.index + step + n) % n];
        new Image().src = this.srcFor(neighbour);
      });
    }

    onClose() {
      document.documentElement.classList.remove('instagram-lightbox-open');
      // Make sure the photo the visitor ended on is visible, and focus it.
      while (this.cells[this.index].hidden) this.showMore(false);
      this.updateStatus();
      this.items[this.index].focus();
    }
  }

  customElements.define('instagram-gallery', InstagramGallery);
}
